import { Component, OnInit, signal, computed, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25RuleTreeModule } from "../../s25-rule-tree/s25.rule.tree.module";

@TypeManagerDecorator("s25-ng-budget-rules")
@Component({
    selector: "s25-ng-budget-rules",
    template: `
        <div class="budget-rules">
            <div class="page-header">
                <h1>Budget Rules</h1>
                <p class="page-description">
                    Configure rules to automatically assign workflow processes to budget plans and organization budgets
                    based on budget criteria such as amount, category, organization, and fiscal constraints.
                </p>
            </div>

            <div class="rules-info-section">
                <div class="info-card">
                    <h3>How Budget Rules Work</h3>
                    <div class="info-content">
                        <div class="rule-flow">
                            <div class="flow-step">
                                <div class="step-number">1</div>
                                <div class="step-content">
                                    <h4>Budget Creation/Modification</h4>
                                    <p>User creates or modifies a budget plan or organization budget</p>
                                </div>
                            </div>
                            <div class="flow-arrow">→</div>
                            <div class="flow-step">
                                <div class="step-number">2</div>
                                <div class="step-content">
                                    <h4>Rule Evaluation</h4>
                                    <p>System evaluates budget against defined conditional rules</p>
                                </div>
                            </div>
                            <div class="flow-arrow">→</div>
                            <div class="flow-step">
                                <div class="step-number">3</div>
                                <div class="step-content">
                                    <h4>Workflow Assignment</h4>
                                    <p>Matching workflow process is automatically assigned</p>
                                </div>
                            </div>
                            <div class="flow-arrow">→</div>
                            <div class="flow-step">
                                <div class="step-number">4</div>
                                <div class="step-content">
                                    <h4>Process Initiation</h4>
                                    <p>Workflow begins with appropriate approval chain</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="examples-card">
                    <h3>Example Rules</h3>
                    <div class="examples-list">
                        <div class="example-item">
                            <strong>High-Value Approval:</strong>
                            <span>If budget amount > $10,000, assign "Executive Approval" workflow</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="rules-editor">
                <div class="editor-header">
                    <h2>Budget Rule Configuration</h2>
                    <p>
                        Define rules to automatically assign workflows based on budget criteria. Rules apply to all
                        budget plans.
                    </p>
                </div>

                <div class="rule-tree-container">
                    <s25-ng-rule-tree
                        [category]="'budgetRules'"
                        [conditionFilterMap]="budgetRulesConditionFilterMap()"
                    ></s25-ng-rule-tree>
                </div>
            </div>
        </div>
    `,
    styles: `
        .budget-rules {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .page-header {
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #dee2e6;
        }

        .page-header h1 {
            margin: 0 0 8px 0;
            color: #333;
            font-size: 2rem;
        }

        .page-description {
            margin: 0;
            color: #666;
            font-size: 14px;
            line-height: 1.5;
        }

        .rules-info-section {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 20px;
            margin-bottom: 24px;
        }

        .info-card,
        .examples-card {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
        }

        .info-card h3,
        .examples-card h3 {
            margin: 0 0 16px 0;
            color: #333;
            font-size: 1.2rem;
        }

        .rule-flow {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
        }

        .flow-step {
            flex: 1;
            min-width: 180px;
            text-align: center;
        }

        .step-number {
            width: 32px;
            height: 32px;
            background: #2196f3;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            margin: 0 auto 8px;
        }

        .step-content h4 {
            margin: 0 0 4px 0;
            font-size: 0.9rem;
            color: #333;
        }

        .step-content p {
            margin: 0;
            font-size: 0.8rem;
            color: #666;
            line-height: 1.3;
        }

        .flow-arrow {
            font-size: 1.5rem;
            color: #2196f3;
            font-weight: bold;
        }

        .examples-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .example-item {
            padding: 12px;
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            border-left: 4px solid #2196f3;
        }

        .example-item strong {
            display: block;
            color: #333;
            margin-bottom: 4px;
            font-size: 0.9rem;
        }

        .example-item span {
            color: #666;
            font-size: 0.85rem;
            line-height: 1.3;
        }

        .rules-editor {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .editor-header {
            background: #f8f9fa;
            padding: 20px;
            border-bottom: 1px solid #dee2e6;
        }

        .editor-header h2 {
            margin: 0 0 8px 0;
            color: #333;
            font-size: 1.4rem;
        }

        .editor-header p {
            margin: 0;
            color: #666;
            font-size: 14px;
            line-height: 1.4;
        }

        .rule-tree-container {
            padding: 20px;
        }

        /* Responsive design */
        @media (max-width: 768px) {
            .budget-rules {
                padding: 12px;
            }

            .rules-info-section {
                grid-template-columns: 1fr;
                gap: 16px;
            }

            .rule-flow {
                flex-direction: column;
                gap: 8px;
            }

            .flow-arrow {
                transform: rotate(90deg);
                font-size: 1.2rem;
            }

            .flow-step {
                min-width: auto;
                width: 100%;
            }

            .page-header h1 {
                font-size: 1.6rem;
            }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
            .info-card,
            .examples-card {
                background: #2d3748;
                border-color: #4a5568;
            }

            .info-card h3,
            .examples-card h3 {
                color: #e2e8f0;
            }

            .step-content h4 {
                color: #e2e8f0;
            }

            .step-content p {
                color: #a0aec0;
            }

            .example-item {
                background: #1a202c;
                border-color: #4a5568;
            }

            .example-item strong {
                color: #e2e8f0;
            }

            .example-item span {
                color: #a0aec0;
            }

            .rules-editor {
                background: #1a202c;
                border-color: #4a5568;
            }

            .editor-header {
                background: #2d3748;
                border-color: #4a5568;
            }

            .editor-header h2 {
                color: #e2e8f0;
            }

            .editor-header p {
                color: #a0aec0;
            }
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25RuleTreeModule],
})
export class BudgetRulesComponent {
    constructor(private cd: ChangeDetectorRef) {}

    // Condition filter map for budgetRules - focused on budget and organization criteria only
    budgetRulesConditionFilterMap = computed(() => {
        return {
            // Workflow processes for assignment targets
            workflowProcesses: "all",
            //Add addtional fields as needed
        };
    });
}
